import { amountPerLabel, amountPeriodLabel } from './amount-per.js';
import { perHead, shortAmount, ratePercent, SUPPORT_VIEW_LABEL, PROGRAM_KIND_LABEL, FACILITY_KIND_LABEL } from './grant-support-view.js';
export const ELIGIBILITY_KIND_LABEL = {
    region: '지역', business_age: '업력', revenue: '매출', employees: '근로자',
    industry: '업종', certification: '인증', exclusion: '제외 대상', other: '기타',
};
/**
 * 한 지원 항목을 **읽을 수 있는 줄들**로 편다.
 *
 * **순서가 뜻이다.** 돈이 먼저, 조건이 다음, 규모가 마지막이다 — 「얼마
 * 주나」가 첫 질문이고 「몇 곳 뽑나」는 그다음이다.
 *
 * **`amountKind` 가 문장을 바꾼다.** 같은 1억 4,000만원이라도 기업당 한도면
 * 「최대」고 총재원이면 「총」이다. 이 구별을 흘리면 독자가 받을 돈을 몇 배로
 * 읽는다.
 */
export function describeSupportRow(row) {
    const facts = [];
    const v = row.values;
    const q = (key) => row.evidence[key]?.quote;
    const num = (key) => (typeof v[key] === 'number' ? v[key] : null);
    const amount = num('maximumAmount');
    // 총재원(fund_total)은 fact 로 세우지 않는다 — 「예산 182억」은 내 몫이 아니다. 머릿수가
    // 있으면 아래에서 「기업당 평균(추정)」으로만 말한다 (2026-09-12 소유자 결정).
    if (amount !== null && row.amountKind !== 'fund_total') {
        const isTotal = false;
        /*
          **분모가 있으면 「최대」 대신 그 말을 쓴다** (`0028`). 「1인당 30만원」을
          「최대 30만원」이라 쓰면 사람 수만큼 적게 말한다. 총재원에는 안 붙인다 —
          「총」은 나누기 전의 값이라 분모와 같이 설 자리가 없다.
        */
        const per = isTotal
            ? null
            : [
                amountPerLabel(typeof v.amountPer === 'string' ? v.amountPer : null),
                amountPeriodLabel(typeof v.amountPeriod === 'string' ? v.amountPeriod : null),
            ].filter(Boolean).join(' ') || null;
        facts.push({
            label: isTotal ? '총 지원 규모' : v.isEquity === true ? '투자 규모' : '지원 한도',
            text: (isTotal ? '총 ' : per ? `${per} ` : '최대 ') +
                shortAmount(amount) +
                (v.isEquity === true ? ' (지분 대가)' : ''),
            quote: q('maximumAmount'),
        });
    }
    // 실비 지원: 상한 숫자는 없지만 「얼마나」의 답은 있다.
    if (amount === null && row.amountKind === 'actual_cost') {
        const basis = q('actualCost') ?? '';
        facts.push({
            label: '지원 금액',
            text: /견적/.test(basis) ? '실비 전액 (견적 기준)' : '실비 지원',
            quote: q('actualCost'),
        });
    }
    const ratio = num('ratio');
    if (ratio !== null) {
        facts.push({ label: '지원 비율', text: `총사업비의 ${ratio}%`, quote: q('ratio') });
    }
    /*
      **자부담은 분모를 밝혀야 뜻이 선다.** 「5,000만원 지원, 자부담 20%」가
      `on_top` 이면 1,000만원을 더 내는 것이고 `included` 면 총사업비 안에서
      나누는 것이다. 분모를 모르면 계산을 안 한다 — 틀린 계산보다 낫다.
    */
    const selfRatio = num('selfPayRatio');
    if (selfRatio !== null) {
        const basis = v.selfPayBasis;
        const suffix = basis === 'on_top' ? ' (지원금과 별도)' : basis === 'included' ? ' (총사업비 기준)' : '';
        facts.push({ label: '자부담', text: `${selfRatio}%${suffix}`, quote: q('selfPayRatio') });
    }
    const selfAmount = num('selfPayAmount');
    if (selfAmount !== null) {
        facts.push({ label: '자부담 금액', text: shortAmount(selfAmount), quote: q('selfPayAmount') });
    }
    /*
      **이율 세 가지는 서로 반대 방향을 가리킨다.** 금리는 내는 것, 이차보전은
      깎아 주는 폭, 보증료율은 보증기관에 내는 수수료다. 라벨을 뭉개면 정반대로
      읽힌다 (docs/SUPPORT-FIELDS.md §4).
    */
    const rate = num('interestRate');
    if (rate !== null)
        facts.push({ label: '금리', text: `연 ${ratePercent(rate)}`, quote: q('interestRate') });
    const subsidy = num('interestSubsidy');
    if (subsidy !== null) {
        facts.push({ label: '이차보전', text: `${ratePercent(subsidy)}p 감면`, quote: q('interestSubsidy') });
    }
    const fee = num('guaranteeFeeRate');
    if (fee !== null)
        facts.push({ label: '보증료율', text: `연 ${ratePercent(fee)}`, quote: q('guaranteeFeeRate') });
    const gRatio = num('guaranteeRatio');
    if (gRatio !== null)
        facts.push({ label: '보증 비율', text: `대출액의 ${gRatio}%`, quote: q('guaranteeRatio') });
    const term = num('loanTermMonths');
    if (term !== null)
        facts.push({ label: '대출 기간', text: monthsText(term), quote: q('loanTermMonths') });
    const grace = num('graceMonths');
    if (grace !== null)
        facts.push({ label: '거치 기간', text: monthsText(grace), quote: q('graceMonths') });
    const duration = num('durationMonths');
    if (duration !== null)
        facts.push({ label: '지원 기간', text: monthsText(duration), quote: q('durationMonths') });
    const capacity = num('capacity');
    if (capacity !== null)
        facts.push({ label: '모집 규모', text: `${capacity.toLocaleString('ko-KR')}개사·건`, quote: q('capacity') });
    // 총재원 ÷ 머릿수 — 「총 182억」을 한 회사 몫으로 오독하지 않게 기업당 추정치를 함께 둔다.
    const total = num('maximumAmount');
    if (row.amountKind === 'fund_total' && total !== null && capacity !== null && capacity > 0) {
        facts.push({ label: '기업당 평균(추정)', text: `약 ${shortAmount(perHead(total, capacity))}` });
    }
    if (typeof v.programKind === 'string') {
        facts.push({ label: '지원 종류', text: PROGRAM_KIND_LABEL[v.programKind] ?? v.programKind });
    }
    if (typeof v.facilityKind === 'string') {
        facts.push({ label: '시설 종류', text: FACILITY_KIND_LABEL[v.facilityKind] ?? v.facilityKind });
    }
    const rent = num('rentDiscountRatio');
    if (rent !== null)
        facts.push({ label: '임대료 감면', text: `${rent}%`, quote: q('rentDiscountRatio') });
    /*
      **사용처는 검증을 안 거친다.** 모델이 쓴 산문이라 원문에 대고 대조한 적이
      없다. 보여주되 다른 것과 같은 무게로 두면 안 된다 (SUPPORT-FIELDS §1).
    */
    if (typeof v.usageScope === 'string' && v.usageScope.trim()) {
        facts.push({ label: '사용처', text: v.usageScope, unverified: true });
    }
    return facts;
}
/** 「12개월」이 아니라 「1년」이라고 말한다. 사람이 그렇게 읽는다. */
function monthsText(months) {
    if (months % 12 === 0 && months >= 12)
        return `${months / 12}년`;
    if (months > 12)
        return `${Math.floor(months / 12)}년 ${months % 12}개월`;
    return `${months}개월`;
}
/**
 * 자격 조건을 유형별로 묶는다.
 *
 * **`exclusion` 은 맨 뒤로 보낸다.** 「받을 수 있는 조건」과 「못 받는 조건」은
 * 반대 방향이라, 섞어 놓으면 제외 사유가 자격 요건으로 읽힌다. 실측에서
 * 자격 조건의 절반 가까이가 결격사유다 — 뭉치면 화면이 결격사유 목록이 된다.
 */
export function groupEligibility(rows) {
    const order = [
        'region', 'business_age', 'revenue', 'employees', 'industry', 'certification', 'other', 'exclusion',
    ];
    return order
        .map((kind) => ({
        kind,
        label: ELIGIBILITY_KIND_LABEL[kind],
        items: rows.filter((r) => r.kind === kind),
    }))
        .filter((g) => g.items.length > 0);
}
/** 유형 묶음의 제목. 카드와 같은 말을 쓴다 — 두 화면이 다른 이름을 쓰면 같은 것으로 안 읽힌다. */
export const SUPPORT_DETAIL_LABEL = SUPPORT_VIEW_LABEL;
//# sourceMappingURL=grant-support-detail.js.map